import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { getData, postData, deleteData, uploadFile } from '../../../webService/webservice.js';
import '../../../styles/Auth/appearance.css';
import { Box, Modal, Button, Typography } from '@mui/material';

const LIMIT_OPTIONS = [5, 10, 25, "all"];

const LoginBackgroundPage = () => {
    const [backgrounds, setBackgrounds] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [limit, setLimit] = useState(5);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [bgIndexToDelete, setBgIndexToDelete] = useState(null);
    const sessionToken = localStorage.getItem("Token");

    useEffect(() => {
        fetchBackgrounds(currentPage);
    }, [currentPage, limit]);

    const fetchBackgrounds = async (page = 1) => {
        try {
            setLoading(true);
            const res = await getData("appearance/backgrounds", {
                page,
                limit: limit === "all" ? 1000 : limit
            });

            if (res?.status === 200) {
                const data = res.data;
                if (data?.results) {
                    setBackgrounds(data.results || []);
                    setCurrentPage(data.currentPage || page);
                    setTotalPages(data.totalPages || 1);
                    setTotalItems(data.totalItems || 0);
                } else if (Array.isArray(data)) {
                    setBackgrounds(data);
                    setTotalItems(data.length);
                    setTotalPages(1);
                }
            }
        } catch (err) {
            console.error("Fetch error:", err);
            toast.error("Failed to load backgrounds");
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const allowedExtensions = /(\.jpg|\.jpeg|\.png)$/i;
        if (!allowedExtensions.exec(file.name)) {
            toast.error("Invalid file type. Only JPEG, JPG, PNG are allowed.");
            return;
        }

        if (file.size > 1 * 1024 * 1024) {
            toast.error("File is too large! Max 1MB allowed.");
            return;
        }

        try {
            setIsUploading(true);
            const userId = localStorage.getItem("userId");
            const uploadRes = await uploadFile(`appearance/backgrounds/upload/${userId}`, file);

            if (uploadRes?.data) {
                await postData("appearance/backgrounds", {
                    fileObject: uploadRes.data,
                }, sessionToken);
                toast.success("Login background added successfully");
                fetchBackgrounds(currentPage);
                if (event.target) event.target.value = "";
            } else {
                toast.error(uploadRes?.message || "Upload failed");
            }
        } catch (error) {
            console.error("Upload error:", error);
            toast.error("Error during upload");
        } finally {
            setIsUploading(false);
        }
    };

    const handleOpen = (frontendIndex) => {
        const originalIndex = totalItems - 1 - frontendIndex;
        setBgIndexToDelete(originalIndex);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setBgIndexToDelete(null);
    };

    const handleDelete = async () => {
        try {
            await deleteData(`appearance/backgrounds/${bgIndexToDelete}`, sessionToken);
            toast.success("Background removed");
            handleClose();
            const newPage = backgrounds.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
            fetchBackgrounds(newPage);
        } catch (err) {
            toast.error("Delete failed");
        }
    };

    const getImageUrl = (bg) => {
        const uri = bg.uri || bg.fileObject?.uri;
        if (!uri) return "";
        const baseUrl = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
        return `${baseUrl}/${uri.replace(/^\/+/, "")}`;
    };

    const handlePageChange = (direction) => {
        setCurrentPage((prev) => {
            if (direction === "first") return 1;
            if (direction === "last") return totalPages;
            if (direction === "next") return Math.min(prev + 1, totalPages);
            if (direction === "prev") return Math.max(prev - 1, 1);
            return prev;
        });
    };

    const handleItemsPerPageChange = (e) => {
        const value = e.target.value;
        setLimit(value === "all" ? "all" : Number(value));
        setCurrentPage(1);
    };

    const startItem = totalItems === 0 ? 0 : (currentPage - 1) * (limit === "all" ? totalItems : limit) + 1;
    const endItem = limit === "all" ? totalItems : Math.min(currentPage * limit, totalItems);

    return (
        <div className="appearance-layout">
            <div className="appearance-card">
                <div className="appearance-header">
                    <div className="title-area">
                        <h2>Login Page Backgrounds</h2>
                        <p>
                            Manage images for the login slideshow
                            {totalItems > 0 && (
                                <span style={{ marginLeft: 8, color: '#94a3b8', fontWeight: 500, fontSize: 13 }}>
                                    — {totalItems} total
                                </span>
                            )}
                        </p>
                    </div>
                </div>

                <div className="upload-section">
                    <label className="upload-placeholder" style={{ opacity: isUploading ? 0.7 : 1 }}>
                        <Icon icon={isUploading ? "mdi:loading" : "mdi:cloud-upload-outline"}
                            className="icon"
                            style={isUploading ? { animation: "spin 0.8s linear infinite" } : {}}
                        />
                        <span>{isUploading ? "Uploading..." : "Click to upload new background (Max 1MB, JPEG/JPG/PNG only)"}</span>
                        <input
                            type="file"
                            accept=".jpeg,.jpg,.png"
                            hidden
                            onChange={handleFileUpload}
                            disabled={isUploading}
                        />
                    </label>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8', fontSize: 15 }}>
                        <Icon icon="mdi:loading" style={{ fontSize: 36, animation: "spin 0.8s linear infinite", display: 'block', margin: '0 auto 12px' }} />
                        Loading backgrounds...
                    </div>
                ) : backgrounds.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8', fontSize: 15 }}>
                        <Icon icon="mdi:image-off-outline" style={{ fontSize: 48, color: '#cbd5e1', display: 'block', margin: '0 auto 12px' }} />
                        No backgrounds uploaded yet
                    </div>
                ) : (
                    <div className="backgrounds-grid">
                        {backgrounds.map((bg, index) => {
                            const imageUrl = getImageUrl(bg);
                            return (
                                <div
                                    key={bg._id || index}
                                    className="background-item"
                                    style={{
                                        backgroundImage: imageUrl ? `url("${imageUrl}")` : "none",
                                        backgroundColor: imageUrl ? "transparent" : "#f0f0f0",
                                        backgroundSize: "cover",
                                        backgroundPosition: "center",
                                        backgroundRepeat: "no-repeat"
                                    }}
                                >
                                    <div className="background-overlay">
                                        <button
                                            className="delete-bg-btn"
                                            onClick={() => handleOpen(index)}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Pagination (Like Category Page) */}
                {!loading && totalItems > 0 && (
                    <div className="category-bottom" style={{ borderTop: '1px solid #f1f5f9', marginTop: 24 }}>
                        <div className="category-section show-entries">
                            <span className="text-label" style={{ color: 'rgba(0, 0, 0, 0.55)', fontSize: 14, fontWeight: 600 }}>Show</span>
                            <select
                                className="entries-select"
                                value={limit}
                                onChange={handleItemsPerPageChange}
                                style={{ margin: '0 6px', padding: '4px 6px', fontSize: 14, borderRadius: 10, border: '1px solid #ccc' }}
                            >
                                {LIMIT_OPTIONS.map(opt => (
                                    <option key={opt} value={opt}>{opt === "all" ? "All" : opt}</option>
                                ))}
                            </select>
                            <span className="text-label" style={{ color: 'rgba(0, 0, 0, 0.55)', fontSize: 14, fontWeight: 600 }}>Entries</span>
                        </div>

                        <div className="showing-range" style={{ color: '#525252', fontSize: 14, fontWeight: 600 }}>
                            {limit === "all"
                                ? `Showing all ${totalItems} entries`
                                : `Showing ${startItem} to ${endItem} of ${totalItems} entries`}
                        </div>

                        <div className="pagination-buttons" style={{ display: 'flex', gap: 12 }}>
                            <PaginationBtn
                                onClick={() => handlePageChange("first")}
                                disabled={currentPage === 1}
                                title="First"
                            >
                                <Icon icon="mdi:chevron-double-left" />
                            </PaginationBtn>
                            <PaginationBtn
                                onClick={() => handlePageChange("prev")}
                                disabled={currentPage === 1}
                                title="Previous"
                            >
                                <Icon icon="mdi:chevron-left" />
                            </PaginationBtn>
                            <PaginationBtn
                                onClick={() => handlePageChange("next")}
                                disabled={currentPage === totalPages}
                                title="Next"
                            >
                                <Icon icon="mdi:chevron-right" />
                            </PaginationBtn>
                            <PaginationBtn
                                onClick={() => handlePageChange("last")}
                                disabled={currentPage === totalPages}
                                title="Last"
                            >
                                <Icon icon="mdi:chevron-double-right" />
                            </PaginationBtn>
                        </div>
                    </div>
                )}

                <Modal open={open} onClose={handleClose}>
                    <Box sx={modalStyle}>
                        <Box sx={deleteIconBadgeStyle}>🗑️</Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: "#2b2b2b" }}>Confirm Remove</Typography>
                        <Typography sx={{ mt: 1.2, fontSize: 14, color: "#666", lineHeight: 1.6 }}>Are you sure you want to remove this background image?</Typography>
                        <Box sx={{ display: "flex", justifyContent: "center", gap: 2.5, mt: 3.5 }}>
                            <Button variant="contained" onClick={handleDelete} className="add-Product-button">Yes, Remove</Button>
                            <Button variant="outlined" onClick={handleClose} className="cancel-Product-button">Cancel</Button>
                        </Box>
                    </Box>
                </Modal>
            </div>

            <style>{`
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                @keyframes deletePulse {
                    0% { transform: scale(1); boxShadow: 0 0 0 0 rgba(60, 0, 8, 0.4); }
                    70% { transform: scale(1.05); boxShadow: 0 0 0 15px rgba(60, 0, 8, 0); }
                    100% { transform: scale(1); boxShadow: 0 0 0 0 rgba(60, 0, 8, 0); }
                }
            `}</style>
        </div>
    );
};

const PaginationBtn = ({ children, onClick, disabled, title }) => (
    <button
        onClick={onClick}
        disabled={disabled}
        title={title}
        style={{
            minWidth: 38,
            height: 38,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            borderRadius: '18px',
            background: disabled ? '#e2e8f0' : 'linear-gradient(#3C0008, #a52a2a)',
            color: disabled ? '#3C0008' : '#ffffff',
            cursor: disabled ? 'not-allowed' : 'pointer',
            fontSize: 18,
            transition: 'all 0.3s ease',
            boxShadow: disabled ? 'none' : '0 2px 8px rgba(60, 0, 8, 0.2)'
        }}
    >
        {children}
    </button>
);

const modalStyle = {
    position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
    width: 380, background: "linear-gradient(135deg, #ffffff, #faf5f6)", borderRadius: "20px",
    boxShadow: "0 25px 70px rgba(0,0,0,0.35)", p: 4, outline: "none", textAlign: "center"
};

const deleteIconBadgeStyle = {
    width: 74, height: 74, borderRadius: "50%", background: "#3C0008",
    display: "flex", alignItems: "center", justifyContent: "center",
    margin: "0 auto 16px auto", color: "#fff", fontSize: 30,
    boxShadow: "0 12px 28px rgba(60, 0, 8, 0.3)", animation: "deletePulse 1.6s infinite ease-in-out"
};

export default LoginBackgroundPage;
